import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, ImageUp, RefreshCcw, UserRound, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { defaultRelationship, personKey, reportingQrBox, scanIsDuplicate, stopScannerAndTracks, validateReportQrImage } from "@/pages/Dashboard/reportingUtils";

const relationships = [["PRIMARY_SUBJECT", "Primary subject"], ["INVOLVED_PERSON", "Involved person"], ["AFFECTED_VICTIM", "Affected / victim"], ["WITNESS", "Witness"], ["CONCERNED_STAFF", "Concerned staff"], ["OTHER", "Other"]];
const context = (person) => [person.program, person.class_name, person.arm, person.position, person.unit].filter(Boolean).join(" — ");
const CAMERA_PHASES = ["starting", "scanning"];

export default function ReportQrScanner({ selectedKeys = [], onAdd }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [manual, setManual] = useState("");
  const [resolved, setResolved] = useState(null);
  const [relationship, setRelationship] = useState("");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const scanner = useRef(null);
  const cleanup = useRef(null);
  const starting = useRef(false);
  const resolving = useRef(false);
  const mounted = useRef(true);
  const phase = useRef("idle");
  const lastScan = useRef({ value: "", at: 0 });
  const fileInput = useRef(null);
  const region = "report-qr-camera";
  const fileRegion = "report-qr-file-reader";

  const transition = (next) => { phase.current = next; if (mounted.current) setStatus(next); };
  const detachAndClean = () => {
    if (cleanup.current) return cleanup.current;
    const instance = scanner.current;
    scanner.current = null;
    const container = document.getElementById(region);
    cleanup.current = stopScannerAndTracks(instance, container).finally(() => { cleanup.current = null; });
    return cleanup.current;
  };
  const stop = async () => { transition("idle"); await detachAndClean(); };

  const resolve = async (decodedText) => {
    const stamp = Date.now();
    if (!decodedText || resolving.current || scanIsDuplicate(lastScan.current, decodedText, stamp)) return;
    lastScan.current = { value: decodedText, at: stamp };
    resolving.current = true;
    transition("resolving");
    setError("");
    // Hide the preview now; do not make resolution wait on browser-specific cleanup.
    void detachAndClean();
    try {
      const { data } = await api.post("/reports/people/resolve-qr", { value: decodedText });
      if (!mounted.current) return;
      setResolved(data);
      setRelationship(defaultRelationship(data));
      transition("resolved");
      navigator.vibrate?.(30);
    } catch (requestError) {
      if (mounted.current) {
        setError(formatApiError(requestError) || "ID card could not be resolved.");
        transition("error");
      }
    } finally { resolving.current = false; }
  };

  const start = async () => {
    if (starting.current || scanner.current || cleanup.current || CAMERA_PHASES.includes(phase.current)) return;
    starting.current = true;
    setError(""); setResolved(null); setFileName(""); transition("starting");
    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras.length) throw new Error("No camera found");
      if (phase.current !== "starting") return;
      const rear = cameras.find((camera) => /back|rear|environment/i.test(camera.label));
      const instance = new Html5Qrcode(region, { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], verbose: false });
      scanner.current = instance;
      await instance.start((rear || cameras[0]).id, { fps: 15, qrbox: reportingQrBox, aspectRatio: 1, disableFlip: false, experimentalFeatures: { useBarCodeDetectorIfSupported: true } }, (text) => resolve(text), () => {});
      if (mounted.current && scanner.current === instance && phase.current === "starting") transition("scanning");
    } catch (cameraError) {
      const instance = scanner.current; scanner.current = null;
      await stopScannerAndTracks(instance, document.getElementById(region));
      if (mounted.current && phase.current === "starting") {
        const message = /notallowed|permission|denied/i.test(String(cameraError?.message || cameraError)) ? "Camera permission was denied. Allow camera access or upload a QR image." : `Camera could not start: ${cameraError?.message || cameraError}`;
        setError(message); transition("error");
      }
    } finally { starting.current = false; }
  };

  const readImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const validationError = validateReportQrImage(file);
    if (validationError) { setError(validationError); transition("error"); return; }
    await stop();
    setResolved(null); setRelationship(""); setFileName(file.name); setError(""); transition("reading-image");
    const reader = new Html5Qrcode(fileRegion, { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], verbose: false });
    try {
      const decodedText = await reader.scanFile(file, false);
      await resolve(decodedText);
    } catch {
      if (mounted.current) { setError("No QR code was found in that image. Choose another image and try again."); transition("error"); }
    } finally { try { await reader.clear(); } catch {} }
  };

  const reset = async () => {
    await stop();
    setResolved(null); setRelationship(""); setError(""); setManual(""); setFileName("");
    lastScan.current = { value: "", at: 0 }; transition("idle");
  };
  const add = () => {
    if (!resolved || !relationship) return;
    if (selectedKeys.includes(personKey(resolved))) { setError(`${resolved.name || "This person"} is already selected.`); return; }
    if (onAdd({ ...resolved, relationship }) !== false) reset();
  };
  useEffect(() => () => {
    mounted.current = false; phase.current = "unmounted";
    const instance = scanner.current; scanner.current = null;
    void stopScannerAndTracks(instance, document.getElementById(region));
  }, []);

  const showCamera = CAMERA_PHASES.includes(status);
  const busy = ["starting", "scanning", "reading-image", "resolving"].includes(status);
  return <div className="space-y-3">
    <button type="button" onClick={() => setOpen(true)} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-[#1B2C5A] px-4 py-2.5 font-medium text-white shadow-sm hover:bg-[#14224A]" data-testid="report-scan-id"><Camera className="h-4 w-4" />Scan ID Card</button>
    {open && <div className="min-w-0 max-w-full overflow-hidden rounded-xl border border-[#1B2C5A]/20 bg-slate-50 p-3 sm:p-4">
      <div className="mb-3 flex items-center justify-between"><b className="text-[#1B2C5A]">Secure ID scanner</b><button type="button" onClick={async () => { await reset(); setOpen(false); }} aria-label="Close scanner"><X className="h-5 w-5" /></button></div>
      {!resolved && <>
        <div className="flex flex-col gap-2 sm:flex-row"><button type="button" onClick={start} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-white disabled:opacity-50" disabled={busy}><Camera className="h-4 w-4" />Start Camera</button><button type="button" onClick={() => fileInput.current?.click()} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#1B2C5A] bg-white px-4 py-2 text-[#1B2C5A] disabled:opacity-50" disabled={busy}><ImageUp className="h-4 w-4" />{fileName ? "Choose Another Image" : "Upload QR Image"}</button><input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={readImage} className="sr-only" data-testid="report-qr-image-input" /></div>
        {showCamera && <div className="mt-3 min-w-0"><div id={region} className="mx-auto min-h-56 w-full max-w-lg overflow-hidden rounded-lg bg-black/5 sm:min-h-64" data-testid="report-qr-camera" />{status === "starting" && <p className="mt-3 text-sm font-medium text-[#1B2C5A]">Camera starting…</p>}{status === "scanning" && <><p className="mt-3 text-sm font-medium text-emerald-800">Point camera at the DFA ID card QR</p><button type="button" onClick={stop} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg border bg-white px-4 py-2"><RefreshCcw className="h-4 w-4" />Stop Scan</button></>}</div>}
        <div id={fileRegion} className="hidden" aria-hidden="true" />
        {status === "reading-image" && <p className="mt-3 text-sm font-medium text-[#1B2C5A]">Reading QR image…</p>}{status === "resolving" && <p className="mt-3 text-sm font-medium text-[#1B2C5A]">Resolving ID card…</p>}{fileName && status !== "reading-image" && <p className="mt-2 text-xs text-slate-500">Selected image: {fileName}</p>}
        <details className="mt-4"><summary className="cursor-pointer text-sm text-slate-600">Manual QR fallback</summary><div className="mt-2 flex flex-col gap-2 sm:flex-row"><input className="min-w-0 flex-1 rounded-lg border p-2" value={manual} onChange={(event) => setManual(event.target.value)} placeholder="Paste QR value for troubleshooting" /><button type="button" onClick={() => resolve(manual)} disabled={!manual || busy} className="rounded-lg bg-slate-700 px-4 py-2 text-white disabled:opacity-50">Resolve</button></div></details>
      </>}
      {resolved && <div className="rounded-xl border-2 border-emerald-300 bg-white p-4" data-testid="resolved-person"><div className="mb-3 text-xs font-semibold uppercase tracking-wide text-emerald-700">ID Card Resolved</div><div className="flex gap-3"><div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full bg-[#1B2C5A] text-white">{resolved.photo ? <img src={resolved.photo} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-5 w-5" />}</div><div className="min-w-0"><div className="font-semibold text-[#1B2C5A]">{resolved.name}</div><div className="text-sm text-slate-600">{resolved.person_type === "student" ? "Student" : "Staff"} · {resolved.identity}</div><div className="text-xs text-slate-500">{context(resolved)}</div></div></div><label className="mt-4 block text-sm font-medium">Relationship<select className="mt-1 w-full rounded-lg border p-2.5" value={relationship} onChange={(event) => setRelationship(event.target.value)}>{relationships.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label><div className="mt-4 flex flex-col gap-2 sm:flex-row"><button type="button" onClick={add} className="rounded-lg bg-[#1B2C5A] px-4 py-2.5 font-medium text-white" data-testid="qr-add-person">Add to Report</button><button type="button" onClick={reset} className="rounded-lg border bg-white px-4 py-2.5 text-[#1B2C5A]">Scan Another / Cancel</button></div></div>}
      {error && <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">{error}</div>}
    </div>}
  </div>;
}
